import { Box, Button, Paper, Stack, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import AttachMoney from '@mui/icons-material/AttachMoney';
import Chat from '@mui/icons-material/Chat';
import Person from '@mui/icons-material/Person';
import { Link } from 'react-router-dom';
import { MessageCountBadge } from '../MatterCard.styles';
import { UserRole, type User } from '~/shared/types/connect';
import { AvatarImage } from '~/shared/components/AvatarImage';
import { useCurrentUser } from '~/shared/hooks/useCurrentUser';

interface Props {
  otherParty: User;
  canRequestRetainer: boolean;
  expertStripeConnected: boolean;
  onRequestRetainer: () => void;
  onMessage: () => void;
  unreadCount?: number;
}

export const OtherPartyCard = ({
  otherParty,
  canRequestRetainer,
  expertStripeConnected,
  onRequestRetainer,
  onMessage,
  unreadCount = 0,
}: Props) => {
  const { user: currentUser, isLoading: isCurrentUserLoading } =
    useCurrentUser();
  const hideProfileLink =
    otherParty.role !== UserRole.EXPERT &&
    (isCurrentUserLoading || currentUser?.role === UserRole.EXPERT);
  const messageButton = (
    <MessageButton
      variant="contained"
      size="medium"
      startIcon={<Chat sx={{ fontSize: 18, width: 18, height: 18 }} />}
      onClick={onMessage}
    >
      Message
    </MessageButton>
  );
  return (
    <StyledPaper variant="outlined">
      <LayoutRow direction="row" alignItems="center">
        <InfoGroup direction="row" flexWrap="wrap" alignItems="center">
          <StyledAvatar
            avatarUrl={otherParty.avatarUrl}
            initialsLabel={otherParty.name ?? ''}
            alt={otherParty.name ?? ''}
          />
          <InfoTextWrap>
            <NameText>{otherParty.name}</NameText>
            {'firm' in otherParty && otherParty.firm && (
              <FirmText variant="body2" color="text.secondary">
                {otherParty.firm}
              </FirmText>
            )}
            {'credentials' in otherParty && otherParty.credentials && (
              <CredentialsText variant="body2" color="text.secondary">
                {otherParty.credentials}
              </CredentialsText>
            )}
          </InfoTextWrap>
        </InfoGroup>
        <ActionButtonsWrap>
          <MessageCountBadge
            badgeContent={unreadCount}
            color="error"
            invisible={unreadCount === 0}
            className="action-message"
          >
            {messageButton}
          </MessageCountBadge>
          {!hideProfileLink && (
            <ViewProfileButton
              component={Link}
              to={
                otherParty.role === UserRole.EXPERT
                  ? `/experts/${otherParty.id}`
                  : `/attorneys/${otherParty.id}`
              }
              variant="outlined"
              size="medium"
              startIcon={
                <Person sx={{ fontSize: 18, width: 18, height: 18 }} />
              }
              className="action-view-profile"
            >
              View Profile
            </ViewProfileButton>
          )}
          {canRequestRetainer && expertStripeConnected && (
            <RequestRetainerButton
              variant="outlined"
              size="medium"
              startIcon={
                <AttachMoney sx={{ fontSize: 18, width: 18, height: 18 }} />
              }
              onClick={onRequestRetainer}
              className="action-request-retainer"
            >
              Request Retainer
            </RequestRetainerButton>
          )}
        </ActionButtonsWrap>
      </LayoutRow>
    </StyledPaper>
  );
};

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2.5),
  marginBottom: theme.spacing(0.5),
  borderRadius: 8,
  boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
}));

const LayoutRow = styled(Stack)(({ theme }) => ({
  flexWrap: 'wrap',
  gap: theme.spacing(2),
  [theme.breakpoints.up('md')]: {
    flexWrap: 'nowrap',
    gap: theme.spacing(3),
  },
}));

const ActionButtonsWrap = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  flexWrap: 'wrap',
  gap: theme.spacing(2),
  flexShrink: 0,
  [theme.breakpoints.down('md')]: {
    width: '100%',
    flexDirection: 'column',
    '& .MuiButton-root': { width: '100%' },
    '& .MuiBadge-root': { width: '100%' },
  },
  [theme.breakpoints.up('md')]: {
    flexBasis: 'auto',
    '& .action-view-profile': { order: 2 },
    '& .action-request-retainer': { order: 3 },
    '& .action-message': { order: 4 },
  },
}));

const InfoGroup = styled(Stack)(({ theme }) => ({
  flex: 1,
  minWidth: 0,
  gap: theme.spacing(2),
  [theme.breakpoints.up('md')]: {
    flexBasis: 'auto',
  },
  [theme.breakpoints.down('md')]: {
    flexBasis: '100%',
  },
}));

const InfoTextWrap = styled(Box)({
  flex: 1,
  minWidth: 0,
});

const StyledAvatar = styled(AvatarImage)({
  width: 56,
  height: 56,
  flexShrink: 0,
});

const NameText = styled('span')(({ theme }) => ({
  display: 'block',
  ...theme.typography.subtitle1,
  fontWeight: 700,
}));

const FirmText = styled(Typography)(({ theme }) => ({
  marginTop: theme.spacing(0.25),
}));

const CredentialsText = styled(Typography)(({ theme }) => ({
  marginTop: theme.spacing(0.25),
}));

const ViewProfileButton = styled(Button)(({ theme }) => ({
  flexShrink: 0,
  width: '100%',
  minWidth: 'auto',
  textTransform: 'none',
  fontWeight: 600,
  [theme.breakpoints.up('md')]: {
    width: 'auto',
    minWidth: 'auto',
  },
})) as typeof Button;

const RequestRetainerButton = styled(Button)(({ theme }) => ({
  flexShrink: 0,
  width: '100%',
  minWidth: 'auto',
  textTransform: 'none',
  fontWeight: 600,
  [theme.breakpoints.up('md')]: {
    width: 'auto',
    minWidth: 'auto',
  },
})) as typeof Button;

const MessageButton = styled(Button)(({ theme }) => ({
  flexShrink: 0,
  width: '100%',
  minWidth: 'auto',
  height: 36,
  fontSize: 14,
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
  backgroundColor: theme.palette.amount?.main,
  color: theme.palette.common.white,
  textTransform: 'none',
  fontWeight: 600,
  '&:hover': {
    backgroundColor: theme.palette.amount?.dark,
  },
  [theme.breakpoints.up('md')]: {
    width: 'auto',
    minWidth: 'auto',
  },
})) as typeof Button;
